import { FC } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CircularProgress, Stack, Typography } from '@mui/material';
import { formEmailValidation } from '@monorepo/client-common';
import { DesignIcon } from '@monorepo/nova-icons';
import { designTokens } from '@monorepo/nova-ui';
import {
  StyledEmailIconWrapper,
  StyledErrorText,
  StyledPillButton,
  StyledPillTextField,
  StyledStepContainer,
} from './quick-purchase-modal.styles';

interface EmailFormValues {
  email: string;
}

interface QuickPurchaseEmailStepProps {
  emailForm: UseFormReturn<EmailFormValues>;
  isLoading: boolean;
  errorMessage: string | null;
  onSubmit: (values: EmailFormValues) => Promise<void>;
}

export const QuickPurchaseEmailStep: FC<QuickPurchaseEmailStepProps> = ({
  emailForm,
  isLoading,
  errorMessage,
  onSubmit,
}) => {
  const { t } = useTranslation(['quick-purchase']);

  return (
    <StyledStepContainer gap={7}>
      <Stack gap={1}>
        <StyledEmailIconWrapper>
          <DesignIcon iconName="Email" useMuiSvgIcon={false} />
        </StyledEmailIconWrapper>
        <Typography
          variant="h3"
          fontWeight={designTokens.typography.fontWeights.bold}
        >
          {t('modal.email_step.title')}
        </Typography>
      </Stack>

      <Typography variant="body1" color="text.regular">
        {t('modal.email_step.subtitle')}
      </Typography>

      <Controller
        name="email"
        control={emailForm.control}
        rules={formEmailValidation()}
        render={({ field, fieldState }) => (
          <StyledPillTextField
            {...field}
            type="email"
            placeholder={t('modal.email_step.email_placeholder')}
            fullWidth
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
          />
        )}
      />

      {errorMessage && <StyledErrorText>{errorMessage}</StyledErrorText>}

      <StyledPillButton
        variant="primary"
        disabled={isLoading}
        onClick={emailForm.handleSubmit(onSubmit)}
        fullWidth
      >
        {isLoading ? (
          <CircularProgress size={20} />
        ) : (
          t('modal.email_step.continue_button')
        )}
      </StyledPillButton>
    </StyledStepContainer>
  );
};
